<template>
  <div
    
    v-if="visible"
    ref="root"
    :class="['changelog', {
      'changelog--mobile': mq.current === 'mobile',
      'changelog--fixed': mq.current !== 'mobile',
      'changelog--beside-chat': mq.current !== 'mobile' && launcherVisible
    }]"
  >
    <transition
      name="changelog-fade"
      mode="out-in"
    >
      <section
        v-if="open"
        key="panel"
        class="changelog__panel"
        aria-live="polite"
      >
        <header class="changelog__header">
          <span class="changelog__header-title">{{ $t('changelog.title') }}</span>
          <button
            type="button"
            class="changelog__close"
            :aria-label="$t('changelog.title')"
            @click="open = false"
          >
            &times;
          </button>
        </header>

        <div class="changelog__body">
          <ChangelogList
            v-if="!loading && items.length"
            :items="items"
          />

          <AmLoading
            v-else-if="loading"
            :isLoading="loading"
            class="changelog__loading"
          />

          <p
            v-else
            class="changelog__empty"
          >
            {{ $t('changelog.empty') }}
          </p>
        </div>
      </section>

      <button
        v-else
        key="trigger"
        type="button"
        class="changelog__trigger"
        @click.stop="toggle"
      >
        <span class="changelog__trigger-dot"></span>
        {{ $t('changelog.trigger') }}
      </button>
    </transition>
  </div>
</template>

<script setup>
import axios from '@interceptor'
import { inject, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import { AmLoading } from '@appmax_npm/ds-appmax-v3'
import { useHttpStore } from '@/stores/http'
import { useChatSupportStore } from '@/stores/chatSupport'
import { storeToRefs } from 'pinia'
import ChangelogList from './ChangelogList.vue'

defineOptions({ name: 'ChangelogTimeline' })

const mq = inject('mq')

const httpStore = useHttpStore()
// launcher do suporte ocupa o mesmo canto: desloca a bolha pra ficar ao lado
const { launcherVisible } = storeToRefs(useChatSupportStore())

const items = ref([])
const loading = ref(false)
const open = ref(true)
const root = ref(null)
const visible = ref(false)

const toggle = async () => {
  open.value = !open.value

  if (open.value && !items.value.length) {
    await fetchChangelogs()
  }
}

const verifySession = async () => {
  if (!localStorage.getItem('user-token')) return false

  try {
    await axios.get(`${httpStore.baseURL}/validate-token`)
    return true
  } catch {
    return false
  }
}

const fetchChangelogs = async () => {
  visible.value = await verifySession()

  if (!visible.value) return

  loading.value = true

  try {
    items.value = (await httpStore.getChangelogs()) ?? []
  } catch {
    // Silent failure: changelog should never break the page.
    items.value = []
  } finally {
    loading.value = false
  }
}

const handleClickOutside = event => {
  if (open.value && root.value && !root.value.contains(event.target)) {
    open.value = false
  }
}

onBeforeMount(fetchChangelogs)
onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>

<style lang="scss" scoped>
.changelog {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: $primaryFont;

  &--mobile {
    width: 100%;
    align-items: stretch;
    padding: 24px;
  }

  &--fixed {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 40;
    transition: right 0.15s ease;
  }

  // So desloca quando o launcher do Chatwoot esta REALMENTE visivel
  // (ver ChatSupport.vue: logado + SDK pronto + dominio liberado). Fixar
  // esse offset incondicionalmente deixava um vao vazio pra quem nao tem
  // o suporte liberado; nao ter offset nenhum empilhava as duas bolhas.
  //
  // A largura vem medida do proprio launcher (--am-chat-launcher-width,
  // publicada por ChatSupport.vue). Cravar o numero aqui acoplava este
  // arquivo a uma largura que ele nao tem como saber, e qualquer troca de
  // copy/i18n do label voltava a sobrepor as bolhas em silencio. O fallback
  // e a largura atual, caso a propriedade ainda nao tenha sido publicada.
  &--beside-chat {
    right: calc(24px + var(--am-chat-launcher-width, 100px) + 10px);
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 18px;
    background-color: #9b6afa;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    box-shadow: 0px 4px 12px rgba(155, 106, 250, 0.35);
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #874ff5;
    }

    &-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #ffffff;
    }
  }

  &__panel {
    position: absolute;
    right: 0;
    bottom: calc(100% + 12px);
    width: 460px;
    max-width: calc(100vw - 48px);
    max-height: 55vh;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid #ece9f6;
    border-radius: 16px;
    box-shadow: 0px 12px 32px rgba(51, 51, 51, 0.14);
    overflow: hidden;
  }

  &--mobile &__panel {
    position: static;
    width: 100%;
    margin-bottom: 12px;
  }

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid #f2f0fa;

    &-title {
      font-size: 18px;
      font-weight: 700;
      color: #333333;
    }
  }

  &__close {
    background: none;
    border: none;
    font-size: 22px;
    line-height: 1;
    color: #9b9b9b;
    cursor: pointer;
    padding: 0 4px;

    &:hover {
      color: #333333;
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 20px 24px 16px;
    overflow-y: auto;
  }

  &__empty {
    font-size: 13px;
    color: #5c5c5c;
    margin: 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
  }
}

.changelog-fade-enter-active,
.changelog-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.changelog-fade-enter-from,
.changelog-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
